/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/exhaustive-deps */
import { Form, redirect, useLoaderData } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';

import { DateValue } from 'react-aria-components';
import SplitLayout from '../components/splitLayout';
import List from '../components/campaigns/list';
import LoadingModal from '../components/loadingModal';
import {
  CampaignStore,
  LetterTemplate, ReminderCampaignCommunicationInformation, SMSSenderInformation, SMSTemplate,
} from '../types/campaigns.type';
import Modal from '../components/modal';
import customIcons from '../utils/icons';
import useCampaignStore from '../components/campaigns/campaignStore';
import { Channel, TemplateOption } from '../types/enums';
import SMS from '../components/campaign-confirmation/smsPreview';
import LetterPreview from '../components/campaign-confirmation/letterPreview';
import SMSGallery from '../components/campaign-confirmation/smsGallery';
import useRequestState from '../hooks/useRequestState';
import PostLetterTemplates from '../services/adapters/letterTemplate';
import GetSmsTemplates from '../services/adapters/smsTemplate';
import Button from '../components/buttons/baseButton';
import useFilterStore from '../components/streetview/filterStore';
import { formatSnapshot } from '../utils/formatters';
import PostAddReminderCampaign from '../services/adapters/addReminderCampaign';
import useCampaignTemplateFileUpload from '../hooks/useCampaignTemplateFileUpload';
import UploadSmsTemplate from '../components/uploadSmsTemplate';
import UploadTemplate from '../components/form-inputs/uploadTemplate';
import InputDate from '../components/form-inputs/inputDate';
import PostReminderCampaignCommunicationInformation from '../services/adapters/reminderCampaignCommunicationInformation';
import AdvancedInput from '../components/form-inputs/advancedInput';

interface SmsState {
  showSms: boolean;
  smsText: string;
  smsTemplateId: number | undefined
}

interface LetterState {
  showLetter: boolean;
  letterUrl: string;
  letterTemplateId: string | undefined
}

export function loader({ params }: { params: { id: number } }) {
  const adapter = new PostReminderCampaignCommunicationInformation();
  return adapter.adaptResponse({ id: params.id });
}

export async function action({ params }: { params: { id: string } }) {
  const { id } = params;
  const { selectedFilters } = useFilterStore.getState();
  const addCampaignAdapter = new PostAddReminderCampaign();
  await addCampaignAdapter.adaptResponse({ id, selectedFilters });
  return redirect('/campaigns');
}

export default function CampaignReminder() {
  const campaignData = useLoaderData<ReminderCampaignCommunicationInformation>();
  const {
    campaignCommunicationIdString,
    name: campaignCommunicationName,
    benefitName,
    initialSnapshot,
    latestSnapshot,
    numberOfInitialHouseholds,
    numberOfEligibleHouseholds,
  } = campaignData;

  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [communicationChannel, setCommunicationChannel] = useState('');
  const [dispatchDate, setDispatchDate] = useState<string | null>(null);
  const [displayValue, setDisplayValue] = useState('Please select a template');
  const [sms, setSms] = useState<SmsState>({
    showSms: false,
    smsText: '',
    smsTemplateId: undefined,
  });
  const [letter, setLetter] = useState<LetterState>({
    showLetter: false,
    letterUrl: '',
    letterTemplateId: undefined,
  });
  const [templateOption, setTemplateOption] = useState<TemplateOption>(TemplateOption.EXISTING);
  const [smsTemplateOption, setSmsTemplateOption] = useState<TemplateOption>(TemplateOption.EXISTING);
  const { addCampaign, setTemplate } = useCampaignStore((state) => state);

  const handleSMSRadioChange = (newValue: TemplateOption) => {
    if (newValue !== smsTemplateOption) {
      setSms({ showSms: false, smsText: '', smsTemplateId: undefined });
    }
    setSmsTemplateOption(newValue);
  };
  const {
    data: smsTemplates,
    handleRequest: handleSmsRequest,
    FallbackWrapper: FallbackSmsWrapper,
  } = useRequestState<SMSSenderInformation>();
  const {
    data: letterTemplates,
    handleRequest: handleLetterRequest,
    FallbackWrapper: FallbackLetterWrapper,
  } = useRequestState<LetterTemplate[]>();
  const {
    templateFileName,
    templateIsUploading,
    templateUploadError,
    resetUpload,
    uploadFile,
  } = useCampaignTemplateFileUpload(benefitName, (s3Key: string) => {
    setTemplate(campaignCommunicationIdString, s3Key);
  });
  const channelDropdownData = {
    id: 'Channel',
    options: [Channel.Letter, Channel.SMS],
  };
  const modalMessage = `This campaign will be be sent to Policy in Practice and drafts generated for review.
  We will be in touch to confirm details of the campaign before it is sent to residents. Would you like to proceed?`;

  const handleModalOpen = () => {
    setModalOpen((previousState) => !previousState);
  };
  const handleLoading = () => {
    setLoading(true);
  };
  const handleCommunicationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (value === 'default') return;
    setCommunicationChannel(value);
  };

  const campaign = {
    'Campaign Name': campaignCommunicationName,
    'Campaign Type': benefitName,
    'Initial Snapshot': formatSnapshot(initialSnapshot, 'long'),
    'Latest Snapshot': formatSnapshot(latestSnapshot, 'long'),
    'Initial Number of Households': numberOfInitialHouseholds.toString(),
    'Current Number of Households': numberOfEligibleHouseholds.toString(),
  };

  const handleSms = (template: SMSTemplate) => {
    setSms({ showSms: true, smsText: template.template, smsTemplateId: template.id });
    setTemplate(campaignCommunicationIdString, template.id);
  };

  const handleLetter = (event: ChangeEvent<HTMLSelectElement>) => {
    const template = letterTemplates?.find((t) => t.id === event.target.value);
    if (!template) return;
    setLetter({
      showLetter: true,
      letterUrl: template.letterTemplateSignedUrl,
      letterTemplateId: template.id,
    });
    setTemplate(campaignCommunicationIdString, template.id);
    setDisplayValue(template.displayText);
  };
  const handleSmsSubmitSuccess = (text: string, id: number) => {
    setTemplate(campaignCommunicationIdString, id);
    setSms({ showSms: true, smsText: text, smsTemplateId: id });
  };
  useEffect(() => {
    if (!campaignData) return;
    const loadTemplates = async (commsChannel: string, benefitType: string) => {
      if (commsChannel === Channel.SMS && smsTemplateOption === TemplateOption.EXISTING) {
        const GetSmsTemplatesInstance = new GetSmsTemplates();
        const getSmsTemplates = GetSmsTemplatesInstance.adaptResponse();
        await handleSmsRequest(getSmsTemplates);
      }
      if (commsChannel === Channel.Letter) {
        const postLetterTemplateInstance = new PostLetterTemplates();
        const postLetterTemplates = postLetterTemplateInstance.adaptResponse(benefitType);
        await handleLetterRequest(postLetterTemplates);
      }
    };
    if (campaignData) {
      const updatedCampaign: CampaignStore = {
        id: campaignCommunicationIdString,
        createdAt: Date.now(),
        status: 'draft',
        snapshot: latestSnapshot,
        campaignName: campaignCommunicationName,
        campaignType: benefitName,
        communicationChannel,
        excludeHouseholds: false,
        excludeHouseholdsByMonth: undefined,
        stagger: false,
        dispatchDate: dispatchDate || null,
      };
      addCampaign(updatedCampaign);
    }

    loadTemplates(communicationChannel, benefitName);
  }, [communicationChannel, benefitName, dispatchDate, smsTemplateOption]);

  if (numberOfEligibleHouseholds === 0) {
    const errorMessage = 'There are no households from the initial campaign who are eligible in the most recent snapshot of data.';
    throw new Error(errorMessage);
  }
  const handleDateChange = (value: DateValue | null | undefined) => {
    setDispatchDate(value ? value.toString() : '');
  };
  const sendForReviewButton = () => (sms.smsText || letter.letterUrl ? (
    <Button
      id="send-campaign-for-review"
      className="button-green"
      label="Send for Review"
      onClick={handleModalOpen}
    />
  ) : <span>Please choose a template</span>);

  // function to conditionally render content based on loading state and modals
  const renderContent = () => {
    if (isModalOpen) {
      if (loading) {
        return (<LoadingModal message="loading" />);
      }

      return (
        <Modal isOpen={isModalOpen} onClose={handleModalOpen}>
          <span className="text-6xl text-primary">{customIcons.mailWarningIcon}</span>
          <h2 className="text-xl font-bold text-primary-dark">Please Confirm</h2>
          <p className="font-thin whitespace-wrap">{modalMessage}</p>
          <div className="w-full flex justify-between p-2">
            <button
              type="button"
              id="cancelModalButton"
              className="p-1 w-1/2 m-2 shadow-inner bg-white rounded-md text-pip-background border"
              onClick={handleModalOpen}
            >
              Cancel
            </button>
            <Form method="post" className="w-1/2" onSubmit={handleLoading} replace>
              <button
                type="submit"
                className="w-full p-1 m-2 bg-primary rounded-md text-white"
              >
                Send for Review
              </button>
            </Form>
          </div>
        </Modal>
      );
    } return (
      <>
        <h1 className="m-2 p-4 text-header font-medium text-primary-dark border-b-2">Preview Campaign</h1>
        <SplitLayout leftWeight="" rightWeight="">
          <div id="reminder-container-left" className="overflow-scroll">
            <List campaign={campaign} />
            { communicationChannel === Channel.Letter && (
              <InputDate name="dispatchDate" onChange={handleDateChange} />
            )}
            <AdvancedInput
              type="dropdown"
              label="Communication Channel"
              {...channelDropdownData}
              name="communicationChannel"
              id="communicationChannel"
              onChange={(event: ChangeEvent<HTMLSelectElement>) => handleCommunicationChange(event)}
              value={communicationChannel}
              required
            />

            <FallbackSmsWrapper loadingMessage="Loading sms template options">
              {communicationChannel === Channel.SMS && (
              <div id="sms-template-choice-container" className="flex flex-col p-2 justify-between font-thin w-full">
                <span className="text-sm">Select SMS Template Option</span>

                <label htmlFor="sms-template-create-option" className="flex items-center gap-2">
                  <input
                    id="sms-template-create-option"
                    type="radio"
                    name="smsTemplateOption"
                    value={TemplateOption.UPLOAD}
                    checked={smsTemplateOption === TemplateOption.UPLOAD}
                    onChange={() => handleSMSRadioChange(TemplateOption.UPLOAD)}
                  />
                  <span className="text-sm">Create a new SMS template</span>
                </label>
                <label htmlFor="sms-template-existing-option" className="flex items-center gap-2">
                  <input
                    id="sms-template-existing-option"
                    type="radio"
                    name="smsTemplateOption"
                    value={TemplateOption.EXISTING}
                    checked={smsTemplateOption === TemplateOption.EXISTING}
                    onChange={() => handleSMSRadioChange(TemplateOption.EXISTING)}
                  />
                  <span className="text-sm">Choose from existing templates</span>
                </label>
              </div>
              )}
            </FallbackSmsWrapper>
            <FallbackLetterWrapper loadingMessage="Loading template options">

              {communicationChannel === Channel.Letter && (
              <>
                <div id="file-upload-choice-container" className="flex flex-col p-2 justify-between font-thin w-full">
                  <span className="text-sm">Select Template Option</span>
                  <label htmlFor="letter-template-upload-option" className="flex items-center gap-2">
                    <input
                      id="letter-template-upload-option"
                      type="radio"
                      name="templateOption"
                      value={TemplateOption.UPLOAD}
                      checked={templateOption === TemplateOption.UPLOAD}
                      onChange={() => {
                        setTemplateOption(TemplateOption.UPLOAD);
                        setLetter({ showLetter: false, letterTemplateId: undefined, letterUrl: '' });
                      }}
                    />
                    <span className="text-sm">Upload a new Word (.docx) document</span>
                  </label>
                  <label htmlFor="letter-template-existing-option" className="flex items-center gap-2">
                    <input
                      id="letter-template-existing-option"
                      type="radio"
                      name="templateOption"
                      value={TemplateOption.EXISTING}
                      checked={templateOption === TemplateOption.EXISTING}
                      onChange={() => setTemplateOption(TemplateOption.EXISTING)}
                    />
                    <span className="text-sm">Choose from existing templates</span>
                  </label>
                </div>

                {templateOption === TemplateOption.UPLOAD && (
                <div className="flex flex-col p-2 justify-between font-thin w-full">
                  <span className="text-sm">Upload New Campaign Template</span>
                  <UploadTemplate
                    uploadFile={uploadFile}
                    isUploading={templateIsUploading}
                    uploadedFileName={templateFileName}
                    error={templateUploadError}
                    reset={resetUpload}
                  />
                </div>
                )}

                {templateOption === TemplateOption.EXISTING && (
                <div>
                  <label htmlFor="letter-template-dropwdown" className="flex flex-col p-2 justify-between font-thin w-full">
                    <span className="text-sm">Choose Existing Letter Template</span>
                    <select
                      id="letter-template-dropdown"
                      value={displayValue}
                      name="letter-template-dropdown"
                      onChange={handleLetter}
                      className="flex-1 p-1 rounded-md align-middle bg-slate-50 border border-pipgrey-light text-piptext-body"
                    >
                      <option value="">{displayValue}</option>
                      {Array.isArray(letterTemplates) && letterTemplates.map((letterTemplate: LetterTemplate) => (
                        <option
                          key={letterTemplate.id}
                          value={letterTemplate.id}
                        >
                          {letterTemplate.displayText}
                        </option>

                      ))}
                    </select>
                  </label>
                </div>
                )}

              </>
              )}
            </FallbackLetterWrapper>
          </div>
          <div id="reminder-container-right" className="flex grow flex-col overflow-scroll">
            {communicationChannel === Channel.SMS && smsTemplateOption === TemplateOption.UPLOAD && (
            <div>
              <UploadSmsTemplate
                key={smsTemplateOption}
                parentText={sms.smsText}
                smsSenderId={smsTemplates?.smsSenderId as string}
                onSubmitSuccess={handleSmsSubmitSuccess}
              />
            </div>
            )}
            {communicationChannel === Channel.SMS && smsTemplateOption === TemplateOption.EXISTING && !sms.smsText && (
              <SMSGallery smsTemplateList={smsTemplates?.smsTemplates ?? []} handleSms={handleSms} />
            )}

            {communicationChannel === Channel.SMS && smsTemplateOption === TemplateOption.EXISTING && sms.showSms && (
            <SMS sender={smsTemplates?.smsSenderId as string} message={sms.smsText} />
            )}
            {letter.showLetter && communicationChannel === Channel.Letter ? <LetterPreview url={letter.letterUrl} /> : ''}
          </div>
        </SplitLayout>
        <div className="w-full m-2 flex justify-center">
          {sendForReviewButton()}
        </div>
      </>

    );
  };
  return (
    <div className="h-screen flex flex-col w-full p-4">
      {renderContent()}
    </div>
  );
}
